import {
  Alert,
  AlertDescription,
  AlertIcon,
  SimpleGrid,
  Stack,
  Checkbox,
} from "@chakra-ui/react";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import ApiErrorMessage from "../../../../components/ApiErrorMessage";
import ObserverRHF from "@raiden/library/components/ReactHookForm/ObserverRHF";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Customer").Customer} customer
 *
 * @param {Props} props
 */
function CustomersCafForm({ customer }) {
  const intl = useIntl();

  /** @type {import("react-hook-form").UseFormReturn<import("./Handler").CafFormValues>} */
  const { control } = useFormContext();

  const preferredYear = usePreferredYear();

  const referenceTaxIncomeYear = useMemo(() => {
    if (preferredYear) {
      return intl.formatDate(
        dayjs(preferredYear.from).subtract(1, "year").toDate(),
        { year: "numeric" },
      );
    }
    return null;
  }, [intl, preferredYear]);

  return (
    <>
      <ApiErrorMessage />

      <Stack spacing="1rem">
        <FormController
          name={`_has_children`}
          renderWithFormControl={(field) => (
            <Checkbox
              {...field}
              isChecked={Boolean(field.value)}
              colorScheme="brandPrimary">
              <FormattedMessage defaultMessage="Avez-vous des enfants qui vont participer aux activités du centre Social Louise Michel ?" />
            </Checkbox>
          )}
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1rem">
          <ObserverRHF
            names={["_has_children"]}
            render={({ values: [_has_children] }) => {
              const hasChidlren = Boolean(_has_children);

              return (
                hasChidlren && (
                  <>
                    <FormController
                      control={control}
                      name="pending_dependent_children_count"
                      label={intl.formatMessage({
                        defaultMessage: "Nombre d'enfants à charge",
                      })}
                      helperText={
                        customer.pending_dependent_children_count !== null
                          ? intl.formatMessage({
                              defaultMessage:
                                "Le nouveau nombre d'enfants à charge est en cours de validation.",
                            })
                          : undefined
                      }
                    />

                    <FormController
                      control={control}
                      name="caf_number"
                      label={intl.formatMessage({
                        defaultMessage: "Numéro d'allocataire CAF",
                      })}
                    />

                    <FormController
                      control={control}
                      name="pending_caf_coeff"
                      label={intl.formatMessage({
                        defaultMessage: "Quotient familial",
                      })}
                      helperText={
                        customer.pending_caf_coeff !== null
                          ? intl.formatMessage({
                              defaultMessage:
                                "Le nouveau quotient familial est en cours de validation.",
                            })
                          : undefined
                      }
                    />

                    <Stack spacing=".5rem">
                      <FormController
                        control={control}
                        name="pending_reference_tax_income"
                        label={intl.formatMessage({
                          defaultMessage: "Revenu fiscal de référence",
                        })}
                        helperText={
                          customer.pending_reference_tax_income !== null
                            ? intl.formatMessage({
                                defaultMessage:
                                  "Le nouveau revenu fiscal de référence est en cours de validation.",
                              })
                            : undefined
                        }
                      />

                      {referenceTaxIncomeYear && (
                        <Alert status="info">
                          <AlertIcon />

                          <AlertDescription>
                            <FormattedMessage
                              defaultMessage="Vous devez spécifier le revenu fiscal de votre foyer pour l'année {year}."
                              values={{ year: referenceTaxIncomeYear }}
                            />
                          </AlertDescription>
                        </Alert>
                      )}
                    </Stack>
                  </>
                )
              );
            }}
          />
        </SimpleGrid>
      </Stack>
    </>
  );
}

export default CustomersCafForm;
